import { useState } from 'react'
import { Button, PickList, ResourceSelectField } from '@krp/ui-kit'
import type { CashierCandidate } from '../../../shared/candidates'
import { sourceOptions, type SourceItem, type SourcesLoad, type SourceKinds } from '../../../shared/sourceOptions'
import { sameSurname } from '../model/profile'
import type { OwnCashier, OwnCashierLink } from '../model/types'

export interface OwnCashierLinksCardProps {
  cashier: OwnCashier
  /** Источники, в которых человек управляет кассирами: только в них можно искать и связывать. */
  sources: SourceItem[]
  kinds?: SourceKinds
  sourcesLoad?: SourcesLoad
  linkResourceId: string
  linkSearch: string
  candidates: CashierCandidate[] | undefined
  candidatesLoading: boolean
  canManage: boolean
  busy: boolean
  chooseSource: (resourceId: string) => void
  find: (value: string) => void
  save: (links: OwnCashierLink[]) => Promise<boolean>
  /** Открыть настройки этого кассира в его источнике: там задаются кассы, на которых ему можно работать, касса и вид цены по умолчанию. */
  openSourceSettings: (link: OwnCashierLink) => void
}

const linkId = (link: { resourceId: string; key: string }) => `${link.resourceId}|${link.key}`
const signature = (links: readonly OwnCashierLink[]) => links.map(linkId).sort().join(',')

/**
 * Где этот кассир есть в источниках: AQSI, 1С, «Собственная». Администратор находит его запись в источнике и связывает; совпадение фамилии —
 * только подсказка, сама связь ставится руками. Один кассир источника — у одного кассира. Оборудование здесь не настраивается: на каждой связи
 * показано, какие кассы доступны кассиру в источнике (задаются в его настройках в источнике). Изменения уходят кнопкой «Сохранить связи».
 */
export function OwnCashierLinksCard(props: OwnCashierLinksCardProps) {
  const [draft, setDraft] = useState<OwnCashierLink[]>(props.cashier.links)
  const dirty = signature(draft) !== signature(props.cashier.links)
  const editable = props.canManage && props.cashier.canEdit && !props.cashier.archived
  const source = props.sources.find((item) => item.id === props.linkResourceId)
  const items = (props.candidates ?? [])
    .filter((candidate) => candidate.directoryEntry === true && !(candidate.ambiguous === true && !candidate.sourceCashierId))
    .map((candidate) => {
      const mine = draft.some((link) => link.resourceId === props.linkResourceId && link.key === candidate.key)
      const foreign = candidate.personId !== null && candidate.personId !== props.cashier.id
      const hint = sameSurname(candidate.name, props.cashier) ? 'совпадает фамилия' : null
      const note = mine ? 'уже в связях' : foreign ? `связан с кассиром «${candidate.personName ?? 'другим'}»` : hint
      return { id: candidate.key, label: candidate.name || candidate.key, note, disabled: !editable || props.busy || mine || foreign, hint: hint !== null }
    })
    .sort((a, b) => Number(b.hint) - Number(a.hint))
  const add = (key: string) => {
    const candidate = props.candidates?.find((item) => item.key === key)
    if (candidate === undefined || source === undefined) return
    setDraft((current) => [...current, { resourceId: source.id, resourceName: source.name, key: candidate.key, name: candidate.name, sourceCashierId: candidate.sourceCashierId ?? null, allowedRegisters: null }])
  }
  return <section className="own-cashiers__card" aria-label="Кассир в источниках">
    <h3>В источниках</h3>
    {draft.length === 0
      ? <p role="status">Кассир ещё не связан ни с одним кассиром источника: в РМК ему некуда войти, а статистики нет.</p>
      : <ul className="own-cashiers__links">{draft.map((link) => {
        // Оборудование берётся из свежего ответа сервера, а не из черновика: оно меняется в настройках кассира источника, пока карточка открыта.
        const allowed = props.cashier.links.find((item) => linkId(item) === linkId(link))?.allowedRegisters ?? []
        return <li key={linkId(link)}>
          <span>{link.name || link.key}</span> <small>{link.resourceName}</small>
          <small> · {allowed.length === 0 ? 'любая касса' : `кассы: ${allowed.length}`}</small>
          {link.sourceCashierId !== null && <Button variant="ghost" actionId="receipts.own-cashiers.source-settings" disabled={props.busy || dirty}
            disabledReason={dirty ? 'Сначала сохраните связи' : null} onClick={() => props.openSourceSettings(link)}>Настройки в источнике</Button>}
          {editable && <Button variant="ghost" actionId="receipts.own-cashiers.remove-link" disabled={props.busy}
            onClick={() => setDraft((current) => current.filter((item) => linkId(item) !== linkId(link)))}>Убрать связь</Button>}
        </li>
      })}</ul>}
    {editable && <>
      <ResourceSelectField label="Найти этого кассира в источнике" actionId="receipts.own-cashiers.link-source" value={props.linkResourceId}
        options={sourceOptions(props.sources, props.kinds)} onChange={props.chooseSource} disabled={props.busy}
        {...(props.sourcesLoad === undefined ? {} : { loading: props.sourcesLoad.loading, loadError: props.sourcesLoad.error, onRetry: props.sourcesLoad.retry })} />
      {source !== undefined && <PickList searchLabel="Найти кассира источника" listLabel={`Кассиры источника «${source.name}»`} busy={props.candidatesLoading}
        items={items.map(({ hint: _hint, ...item }) => item)} query={props.linkSearch} onQuery={props.find}
        emptyText="В источнике нет кассиров." notFoundText="Кассир не найден." onPick={add} />}
    </>}
    {dirty && <p role="status">● Связи изменены и ещё не сохранены.</p>}
    {editable && <div className="own-cashiers__actions">
      <Button variant="primary" actionId="receipts.own-cashiers.save-links" disabled={!dirty || props.busy} busy={props.busy}
        onClick={() => { void props.save(draft) }}>Сохранить связи</Button>
      {dirty && <Button actionId="receipts.own-cashiers.remove-link" disabled={props.busy} onClick={() => setDraft(props.cashier.links)}>Вернуть как было</Button>}
    </div>}
    {!props.cashier.canEdit && props.cashier.links.length > 0 && <p role="status">Связи меняет тот, кто управляет всеми источниками этого кассира.</p>}
  </section>
}
